import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import If from '@components/if/if.tsx';
import ChartLoader from '@shared/components/charts/chart-loader/chart-loader.tsx';
import ChartEmpty from '@shared/components/charts/chart-empty/chart-empty.tsx';
import MultiLineChartTooltip from '@shared/components/charts/multi-line-chart/components/multi-line-chart-tooltip/multi-line-chart-tooltip.tsx';
import {
  MultiLineChartProps,
  MultiLineChartLine
} from '@shared/models/charts/multi-line-chart.model.ts';
import { Typography } from '@mui/material';

const LEGEND_HEIGHT = 25;

const MultiLineChart = <T extends Record<string, unknown>>({
  data,
  loading = false,
  config
}: MultiLineChartProps<T>) => {
  const {
    xAxisKey,
    lines,
    height = 300,
    showDots = true,
    yAxisFormatter,
    tooltipValueFormatter,
    xAxisFormatter,
    tooltipLabelFormatter,
    curveType = 'monotone'
  } = config;

  return (
    <div
      className="bg-(--white) rounded-xl p-4 overflow-hidden relative"
      style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}
    >
      <If
        loading={loading}
        condition={!loading && data.length > 0}
        loadingContent={<ChartLoader height={height + LEGEND_HEIGHT} />}
        emptyContent={<ChartEmpty height={height + LEGEND_HEIGHT} />}
      >
        <div className="flex items-center justify-center gap-4 py-1">
          {lines.map((line) => (
            <div
              key={String(line.dataKey)}
              className="flex items-center gap-1.5"
            >
              <div
                className="w-3.5 h-3.5 rounded-full shrink-0"
                style={{ backgroundColor: line.color }}
              />

              <Typography variant="body2" className="text-(--blue-4)!">
                {line.label}
              </Typography>
            </div>
          ))}
        </div>

        <ResponsiveContainer width="100%" height={height}>
          <RechartsLineChart
            data={data as Record<string, unknown>[]}
            margin={{ top: 20, right: 10, left: 0, bottom: 20 }}
          >
            <CartesianGrid
              stroke="var(--gray-7)"
              strokeDasharray="3 3"
              strokeWidth={1}
            />
            <XAxis
              dataKey={xAxisKey as string}
              axisLine={false}
              tickLine={false}
              padding={{ left: 10, right: 10 }}
              tick={{ fill: 'var(--blue-4)', fontSize: 12 }}
              tickMargin={20}
              tickFormatter={xAxisFormatter}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'var(--blue-4)', fontSize: 12 }}
              tickFormatter={yAxisFormatter}
            />
            <Tooltip
              content={
                <MultiLineChartTooltip
                  lines={lines as MultiLineChartLine<Record<string, unknown>>[]}
                  tooltipValueFormatter={tooltipValueFormatter}
                  tooltipLabelFormatter={tooltipLabelFormatter}
                />
              }
            />
            {lines.map((line) => (
              <Line
                key={String(line.dataKey)}
                type={curveType}
                dataKey={String(line.dataKey)}
                stroke={line.color}
                strokeWidth={1.5}
                dot={
                  showDots
                    ? {
                        stroke: line.color,
                        strokeWidth: 1.5,
                        fill: 'var(--white)',
                        r: 3
                      }
                    : false
                }
                activeDot={
                  showDots
                    ? {
                        stroke: line.color,
                        strokeWidth: 1.5,
                        fill: line.color,
                        r: 4
                      }
                    : undefined
                }
              />
            ))}
          </RechartsLineChart>
        </ResponsiveContainer>
      </If>
    </div>
  );
};

export default MultiLineChart;
